import apiClient from './apiClient';
import { FEATURE_INTEGRATION_BIGQUERY } from '../constants/capabilities';

const CAPABILITY_CACHE_TTL_MS = 5000;
let capabilityCache = { data: null, fetchedAt: 0 };

const isCapabilityDisabled = (capabilities) => {
  const featureMap = capabilities?.features;
  if (!featureMap || typeof featureMap !== 'object') return false;
  return featureMap[FEATURE_INTEGRATION_BIGQUERY] === false;
};

const getCachedCapabilities = async () => {
  const now = Date.now();
  if (capabilityCache.data && now - capabilityCache.fetchedAt < CAPABILITY_CACHE_TTL_MS) {
    return capabilityCache.data;
  }
  try {
    const response = await apiClient.get('/auth/me/capabilities', { suppressGlobalErrorToast: true });
    capabilityCache = { data: response.data, fetchedAt: now };
    return response.data;
  } catch {
    return null;
  }
};

export const bigqueryApi = {
  invalidateCapabilityCache: () => {
    capabilityCache = { data: null, fetchedAt: 0 };
  },
  getStatus: async () => {
    const capabilities = await getCachedCapabilities();
    if (isCapabilityDisabled(capabilities)) {
      return {
        connected: false,
        billing_project_id: null,
        reauth_required: false,
        capability_disabled: true
      };
    }
    try {
      const response = await apiClient.get('/bigquery/status', { suppressGlobalErrorToast: true });
      return response.data;
    } catch (error) {
      const status = error?.response?.status;
      const errorCode = error?.response?.data?.error;
      // Expired trial also returns 403 via capability checks: treat as inaccessible integration.
      if (status === 403) {
        return {
          connected: false,
          billing_project_id: null,
          reauth_required: false,
          capability_disabled: true,
          trial_expired: errorCode === 'TRIAL_EXPIRED'
        };
      }
      throw error;
    }
  },
  getLoginUrl: async () => {
    const response = await apiClient.get('/google/login-url');
    return response.data;
  },
  getProjects: async () => {
    const response = await apiClient.get('/bigquery/projects');
    return response.data;
  },
  getDatasets: async (projectId) => {
    const response = await apiClient.get(`/bigquery/projects/${projectId}/datasets`);
    return response.data;
  },
  getTables: async (projectId, datasetId) => {
    const response = await apiClient.get(`/bigquery/projects/${projectId}/datasets/${datasetId}/tables`);
    return response.data;
  },
  disconnect: async () => {
    const response = await apiClient.post('/bigquery/disconnect');
    return response.data;
  },
  setBillingProject: async (projectId) => {
    const response = await apiClient.post('/bigquery/billing-project', { project_id: projectId });
    return response.data;
  },
  listRoutines: async (projectId, datasetId) => {
    const response = await apiClient.get(`/bigquery/projects/${projectId}/datasets/${datasetId}/routines`);
    return response.data;
  }
};
